// src/admin/AdminJourneyHostels.js

import React, { useState, useEffect } from 'react';
import HostelPreview from './HostelPreview';
import * as hostelService from '../services/hostelService'; 

// Helper function to convert image to WebP
const convertImageToWebP = async (file, maxWidth = 2000, maxHeight = 2000, quality = 1.0) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio; height *= ratio;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Try WebP first
        canvas.toBlob((blob) => {
          if (blob) {
            const webpFileName = file.name.replace(/\.[^/.]+$/, '') + '.webp';
            const webpFile = new File([blob], webpFileName, { type: 'image/webp' });
            resolve(webpFile);
          } else {
            // Fallback to PNG if WebP not supported
            canvas.toBlob((pngBlob) => {
              if (pngBlob) {
                const pngFileName = file.name.replace(/\.[^/.]+$/, '') + '.png';
                const pngFile = new File([pngBlob], pngFileName, { type: 'image/png' });
                resolve(pngFile);
              } else {
                reject(new Error('Failed to convert image'));
              }
            }, 'image/png', 1.0);
          }
        }, 'image/webp', quality);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

// Utility to parse the filename from a Firebase Storage URL
function getFileNameFromUrl(url) {
  try {
    const decodedUrl = decodeURIComponent(url);
    const match = decodedUrl.match(/([^/]+)\?*/);
    if (match && match[1]) {
      return match[1].split('?')[0];
    }
    return 'unknown_file';
  } catch {
    return 'unknown_file';
  }
}

const VIBE_OPTIONS = [
  { value: 'clean', label: 'Clean' },
  { value: 'cozy', label: 'Cozy' },
  { value: 'lit', label: 'Lit' }
];

function AdminJourneyHostels({
  hostels,
  selectedHostel,
  handleHostelInputChange,
  handleEditHostel,
  handleDeleteHostel,
  cities,
  handleSaveHostelChanges,
  isMobile = false,
}) {
  const [imageList, setImageList] = useState([]);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [showHostelPreview, setShowHostelPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    if (!selectedHostel?.id) {
      setImageList([]);
      return;
    }
    const existing = (selectedHostel.images || []).map((url) => ({
      isNew: false,
      url,
      file: null,
      name: getFileNameFromUrl(url),
      selected: true,
    }));
    setImageList(existing);
  }, [selectedHostel]);

  const ImageValidator = ({ url }) => {
    const [isValidImage, setIsValidImage] = useState(true);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const img = new Image();
      img.onload = () => {
        setIsValidImage(true);
        setLoading(false);
      };
      img.onerror = () => {
        setIsValidImage(false);
        setLoading(false);
      };
      img.src = url;
    }, [url]);

    if (loading) return <span>Checking...</span>;
    if (!isValidImage) {
      return (
        <span
          style={{
            color: 'red',
            marginRight: '10px',
            display: 'inline-flex',
            alignItems: 'center',
          }}
          title="This image may not load correctly"
        >
          ⚠️
        </span>
      );
    }
    return null;
  };

  const handleHostelImageFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    const newFileObjs = selectedFiles.map((file) => ({
      isNew: true,
      file,
      url: null,
      name: file.name,
      selected: true,
    }));

    setImageList((prev) => [...prev, ...newFileObjs]);
  };

  const handleRemoveNewImage = (index) => {
    setImageList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleToggleSelectNewImage = (index) => {
    setImageList((prev) =>
      prev.map((img, i) => (i === index ? { ...img, selected: !img.selected } : img))
    );
  };

  const handleMoveImage = (index, direction) => {
    setImageList((prev) => {
      const newList = [...prev];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= newList.length) return newList;
      const temp = newList[index];
      newList[index] = newList[targetIndex];
      newList[targetIndex] = temp;
      return newList;
    });
  };

  const handleRemoveExistingImage = async (hostelId, url, index) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    try {
      await hostelService.deleteHostelImage(hostelId, url);
      setImageList((prev) => prev.filter((imgObj) => imgObj.url !== url));
    } catch (err) {
      alert(`Failed to delete image: ${err.message || 'Unknown error'}`);
    }
  };

  const handleSaveAllHostelData = async () => {
    setIsSaving(true);
    setUploadErrors([]);

    if (!selectedHostel.name || !selectedHostel.cityId) {
      alert('Hostel name and city are required.');
      setIsSaving(false);
      return;
    }

    let currentHostelData = { ...selectedHostel };
    let hostelIdToUse = selectedHostel.id;
    let isNewHostelFlow = !selectedHostel.id;

    if (isNewHostelFlow) {
      try {
        console.log("Attempting to create new hostel:", selectedHostel);
        const payloadForCreate = {
          name: selectedHostel.name,
          cityId: selectedHostel.cityId,
          link: selectedHostel.link || '',
          notes: selectedHostel.notes || '',
          description: selectedHostel.description || selectedHostel.notes || '',
          info: selectedHostel.info || '',
          contact: selectedHostel.contact || '',
          amadeusHotelId: selectedHostel.amadeusHotelId || '',
          vibe: selectedHostel.vibe || 'clean',
          cashRequired: selectedHostel.cashRequired || false,
          latitude: selectedHostel.latitude ? parseFloat(selectedHostel.latitude) : null,
          longitude: selectedHostel.longitude ? parseFloat(selectedHostel.longitude) : null,
          images: [],
          ratingScore: selectedHostel.ratingScore ? parseFloat(selectedHostel.ratingScore) : null,
          ratingCount: selectedHostel.ratingCount ? parseInt(selectedHostel.ratingCount, 10) : null,
          cheapestPrice: selectedHostel.cheapestPrice ? parseFloat(selectedHostel.cheapestPrice) : null,
          currency: selectedHostel.currency || 'USD',
          amenities: selectedHostel.amenities || [],
          roomOffers: selectedHostel.roomOffers || [],
        };
        const newHostel = await hostelService.createHostel(payloadForCreate);
        if (!newHostel || !newHostel.id) {
          throw new Error("Hostel creation failed or did not return an ID.");
        }
        hostelIdToUse = newHostel.id;
        currentHostelData = { ...newHostel };
        handleEditHostel(currentHostelData);
        console.log("New hostel created with ID:", hostelIdToUse);

      } catch (err) {
        alert(`Failed to create new hostel: ${err.message || 'Unknown error'}`);
        setIsSaving(false);
        return;
      }
    }

    const newImageFilesToUpload = imageList.filter((img) => img.isNew && img.selected && img.file);
    const successfullyUploadedImagesMap = new Map();

    if (newImageFilesToUpload.length > 0) {
      console.log(`Uploading ${newImageFilesToUpload.length} new images for hostel ID: ${hostelIdToUse}`);
      for (const imgObj of newImageFilesToUpload) {
        try {
          // Convert to WebP before uploading
          const webpFile = await convertImageToWebP(imgObj.file);
          const uploadedUrl = await hostelService.uploadHostelImage(hostelIdToUse, webpFile);
          successfullyUploadedImagesMap.set(imgObj.name, uploadedUrl);
          if (imgObj.previewUrl) URL.revokeObjectURL(imgObj.previewUrl);
        } catch (err) {
          console.error('Upload error for:', imgObj.name, err);
          setUploadErrors((prev) => [...prev, `Upload failed for ${imgObj.name}: ${err.message || 'Unknown error'}`]);
        }
      }
    }

    let workingImageList = [...imageList];

    for (const [originalFileName] of successfullyUploadedImagesMap) {
      const conflictingExistingImageIndex = workingImageList.findIndex(
        (exImg) => !exImg.isNew && getFileNameFromUrl(exImg.url) === originalFileName
      );

      if (conflictingExistingImageIndex !== -1) {
        const conflictingImage = workingImageList[conflictingExistingImageIndex];
        console.log(`New image "${originalFileName}" conflicts with existing image. Deleting old: ${conflictingImage.url}`);
        try {
          await hostelService.deleteHostelImage(hostelIdToUse, conflictingImage.url);
          workingImageList.splice(conflictingExistingImageIndex, 1);
        } catch (err) {
          console.warn(`Failed to auto-delete overwritten existing image ${conflictingImage.name}:`, err);
          setUploadErrors((prev) => [...prev, `Failed to auto-delete old ${conflictingImage.name} (replaced by new): ${err.message || 'Unknown error'}`]);
        }
      }
    }

    const finalImageUrls = workingImageList
      .map(img => {
        if (img.isNew) {
          return img.selected ? successfullyUploadedImagesMap.get(img.name) : null;
        }
        return img.url;
      })
      .filter(url => url != null);

    const finalHostelPayload = {
      ...selectedHostel,
      id: hostelIdToUse,
      images: finalImageUrls,
      name: selectedHostel.name,
      cityId: selectedHostel.cityId,
      link: selectedHostel.link,
      notes: selectedHostel.notes,
      description: selectedHostel.description || selectedHostel.notes || '',
      info: selectedHostel.info || '',
      contact: selectedHostel.contact,
      amadeusHotelId: selectedHostel.amadeusHotelId,
      vibe: selectedHostel.vibe || 'chill',
      cashRequired: selectedHostel.cashRequired || false,
      latitude: selectedHostel.latitude,
      longitude: selectedHostel.longitude,
      ratingScore: selectedHostel.ratingScore,
      ratingCount: selectedHostel.ratingCount,
      cheapestPrice: selectedHostel.cheapestPrice,
      currency: selectedHostel.currency,
      amenities: selectedHostel.amenities,
      roomOffers: selectedHostel.roomOffers,
    };

    try {
      console.log("Final payload to be passed to parent's onSave:", finalHostelPayload);
      const savedHostelFromServer = await handleSaveHostelChanges(finalHostelPayload);

      const totalNewAttempted = newImageFilesToUpload.length;
      const currentLocalUploadErrorsCount = uploadErrors.length;

      if (currentLocalUploadErrorsCount > 0) {
        if (currentLocalUploadErrorsCount < totalNewAttempted) {
          alert('Hostel saved. Some new images failed to upload.');
        } else {
          alert('Hostel saved, but all new image uploads failed.');
        }
      } else if (totalNewAttempted > 0 && savedHostelFromServer) {
        alert('Hostel and new images saved successfully!');
      } else if (savedHostelFromServer) {
        console.log('Hostel data saved. Parent handles main success alert.');
      }
      setUploadErrors([]);

    } catch (err) {
      alert(`Failed to save hostel updates: ${err.message || 'Unknown error'}`);
      console.error('Error during parent save operation or subsequent logic:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const cityObj = cities.find((c) => c.id === selectedHostel.cityId);
  const cityName = cityObj ? cityObj.name : 'Not specified';

  // Filter and sort hostels
  const filteredAndSortedHostels = React.useMemo(() => {
    let filtered = hostels.filter(hostel => {
      const cityData = cities.find((c) => c.id === hostel.cityId);
      const cityName = cityData ? cityData.name : '';
      const searchLower = searchTerm.toLowerCase();
      
      return hostel.name.toLowerCase().includes(searchLower) ||
             cityName.toLowerCase().includes(searchLower) ||
             (hostel.notes && hostel.notes.toLowerCase().includes(searchLower)) ||
             (hostel.contact && hostel.contact.toLowerCase().includes(searchLower)) ||
             (hostel.amadeusHotelId && hostel.amadeusHotelId.toLowerCase().includes(searchLower));
    });

    return filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'city':
          const aCityData = cities.find((c) => c.id === a.cityId);
          const bCityData = cities.find((c) => c.id === b.cityId);
          aValue = aCityData ? aCityData.name.toLowerCase() : '';
          bValue = bCityData ? bCityData.name.toLowerCase() : '';
          break;
        case 'vibe':
          aValue = a.vibe || 'clean';
          bValue = b.vibe || 'clean';
          break;
        case 'notes':
          aValue = a.notes || '';
          bValue = b.notes || '';
          break;
        case 'contact':
          aValue = a.contact || '';
          bValue = b.contact || '';
          break;
        default:
          return 0;
      }

      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [hostels, cities, searchTerm, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return (
    <div style={{ 
      marginBottom: isMobile ? '20px' : '30px',
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
      boxSizing: 'border-box'
    }}>
      <h3 style={{ 
        fontSize: isMobile ? '16px' : '20px',
        marginBottom: isMobile ? '10px' : '20px'
      }}>
        Manage Hostels
      </h3>

      {/* HOSTEL FORM */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? '8px' : '16px'
      }}>
        <label style={{
          display: 'flex',
          flexDirection: 'column',
          fontSize: isMobile ? '13px' : '16px',
          fontWeight: 'bold'
        }}>
          Hostel Name:
          <input
            type="text"
            name="name"
            value={selectedHostel.name}
            onChange={handleHostelInputChange}
            style={{
              padding: isMobile ? '6px' : '10px',
              fontSize: isMobile ? '13px' : '16px',
              border: '1px solid #ccc',
              borderRadius: '3px',
              marginTop: '5px'
            }}
          />
        </label>

        <label style={{
          display: 'flex',
          flexDirection: 'column',
          fontSize: isMobile ? '13px' : '16px',
          fontWeight: 'bold'
        }}>
          City:
          <select
            name="cityId"
            value={selectedHostel.cityId}
            onChange={handleHostelInputChange}
            style={{
              padding: isMobile ? '6px' : '10px',
              fontSize: isMobile ? '13px' : '16px',
              border: '1px solid #ccc',
              borderRadius: '3px',
              marginTop: '5px'
            }}
          >
            <option value="">Select City</option>
            {cities.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label style={{
          display: 'flex',
          flexDirection: 'column',
          fontSize: isMobile ? '13px' : '16px',
          fontWeight: 'bold'
        }}>
          Vibe:
          <select
            name="vibe"
            value={selectedHostel.vibe || 'clean'}
            onChange={handleHostelInputChange}
            style={{
              padding: isMobile ? '6px' : '10px',
              fontSize: isMobile ? '13px' : '16px',
              border: '1px solid #ccc',
              borderRadius: '3px',
              marginTop: '5px'
            }}
          >
            {VIBE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label style={{
          display: 'flex',
          alignItems: 'center',
          fontSize: isMobile ? '13px' : '16px',
          fontWeight: 'bold',
          gap: '6px'
        }}>
          <input
            type="checkbox"
            name="cashRequired"
            checked={selectedHostel.cashRequired || false}
            onChange={(e) => handleHostelInputChange({
              target: {
                name: 'cashRequired',
                value: e.target.checked
              }
            })}
            style={{
              width: isMobile ? '14px' : '18px',
              height: isMobile ? '14px' : '18px'
            }}
          />
          Cash payment required at desk
        </label>

        <label style={{
          display: 'flex',
          flexDirection: 'column',
          fontSize: isMobile ? '13px' : '16px',
          fontWeight: 'bold'
        }}>
          Link:
          <input
            type="text"
            name="link"
            value={selectedHostel.link}
            onChange={handleHostelInputChange}
            style={{
              padding: isMobile ? '6px' : '10px',
              fontSize: isMobile ? '13px' : '16px',
              border: '1px solid #ccc',
              borderRadius: '3px',
              marginTop: '5px'
            }}
          />
        </label>

        <label style={{
          display: 'flex',
          flexDirection: 'column',
          fontSize: isMobile ? '13px' : '16px',
          fontWeight: 'bold'
        }}>
          Description:
          <textarea
            name="description"
            value={selectedHostel.description || ''}
            onChange={handleHostelInputChange}
            placeholder="Main description shown in the hostel detail view"
            rows={3}
            style={{
              padding: isMobile ? '6px' : '10px',
              fontSize: isMobile ? '13px' : '16px',
              border: '1px solid #ccc',
              borderRadius: '3px',
              marginTop: '5px',
              resize: 'vertical'
            }}
          />
        </label>

        <label style={{
          display: 'flex',
          flexDirection: 'column',
          fontSize: isMobile ? '13px' : '16px',
          fontWeight: 'bold'
        }}>
          Info:
          <textarea
            name="info"
            value={selectedHostel.info || ''}
            onChange={handleHostelInputChange}
            placeholder="Additional information (check-in times, policies, etc.)"
            rows={3}
            style={{
              padding: isMobile ? '6px' : '10px',
              fontSize: isMobile ? '13px' : '16px',
              border: '1px solid #ccc',
              borderRadius: '3px',
              marginTop: '5px',
              resize: 'vertical'
            }}
          />
        </label>

        <label style={{
          display: 'flex',
          flexDirection: 'column',
          fontSize: isMobile ? '13px' : '16px',
          fontWeight: 'bold'
        }}>
          Notes (Legacy):
          <input
            type="text"
            name="notes"
            value={selectedHostel.notes}
            onChange={handleHostelInputChange}
            placeholder="Legacy field - use Description/Info instead"
            style={{
              padding: isMobile ? '6px' : '10px',
              fontSize: isMobile ? '13px' : '16px',
              border: '1px solid #ccc',
              borderRadius: '3px',
              marginTop: '5px',
              opacity: 0.7
            }}
          />
        </label>

        <label style={{
          display: 'flex',
          flexDirection: 'column',
          fontSize: isMobile ? '13px' : '16px',
          fontWeight: 'bold'
        }}>
          Contact:
          <input
            type="text"
            name="contact"
            value={selectedHostel.contact}
            onChange={handleHostelInputChange}
            style={{
              padding: isMobile ? '6px' : '10px',
              fontSize: isMobile ? '13px' : '16px',
              border: '1px solid #ccc',
              borderRadius: '3px',
              marginTop: '5px'
            }}
          />
        </label>

        <label style={{
          display: 'flex',
          flexDirection: 'column',
          fontSize: isMobile ? '13px' : '16px',
          fontWeight: 'bold'
        }}>
          Amenities:
          <textarea
            name="amenities"
            value={Array.isArray(selectedHostel.amenities) ? selectedHostel.amenities.join(', ') : selectedHostel.amenities || ''}
            onChange={(e) => {
              const amenitiesString = e.target.value;
              const amenitiesArray = amenitiesString.split(',').map(s => s.trim()).filter(Boolean);
              handleHostelInputChange({
                target: {
                  name: 'amenities',
                  value: amenitiesArray
                }
              });
            }}
            placeholder="Enter amenities separated by commas (e.g., wifi, breakfast, locker, laundry)"
            rows={2}
            style={{
              padding: isMobile ? '6px' : '10px',
              fontSize: isMobile ? '13px' : '16px',
              border: '1px solid #ccc',
              borderRadius: '3px',
              marginTop: '5px',
              resize: 'vertical'
            }}
          />
        </label>

        <label style={{
          display: 'flex',
          flexDirection: 'column',
          fontSize: isMobile ? '13px' : '16px',
          fontWeight: 'bold'
        }}>
          Amadeus Hotel ID:
          <input
            type="text"
            name="amadeusHotelId"
            value={selectedHostel.amadeusHotelId || ''}
            onChange={handleHostelInputChange}
            style={{
              padding: isMobile ? '6px' : '10px',
              fontSize: isMobile ? '13px' : '16px',
              border: '1px solid #ccc',
              borderRadius: '3px',
              marginTop: '5px'
            }}
          />
        </label>

        <label style={{
          display: 'flex',
          flexDirection: 'column',
          fontSize: isMobile ? '13px' : '16px',
          fontWeight: 'bold'
        }}>
          Latitude:
          <input
            type="number"
            step="0.000001"
            name="latitude"
            value={selectedHostel.latitude || ''}
            onChange={handleHostelInputChange}
            style={{
              padding: isMobile ? '6px' : '10px',
              fontSize: isMobile ? '13px' : '16px',
              border: '1px solid #ccc',
              borderRadius: '3px',
              marginTop: '5px'
            }}
          />
        </label>

        <label style={{
          display: 'flex',
          flexDirection: 'column',
          fontSize: isMobile ? '13px' : '16px',
          fontWeight: 'bold'
        }}>
          Longitude:
          <input
            type="number"
            step="0.000001"
            name="longitude"
            value={selectedHostel.longitude || ''}
            onChange={handleHostelInputChange}
            style={{
              padding: isMobile ? '6px' : '10px',
              fontSize: isMobile ? '13px' : '16px',
              border: '1px solid #ccc',
              borderRadius: '3px',
              marginTop: '5px'
            }}
          />
        </label>
      </div>

      {/* IMAGES SECTION */}
      <div style={{ 
        margin: isMobile ? '15px 0' : '10px 0',
        padding: isMobile ? '10px' : '0',
        border: isMobile ? '1px solid #eee' : 'none',
        borderRadius: isMobile ? '5px' : '0'
      }}>
        <h4 style={{
          fontSize: isMobile ? '16px' : '18px',
          marginBottom: isMobile ? '10px' : '15px'
        }}>Hostel Images</h4>
        <p style={{
          fontSize: isMobile ? '13px' : '16px',
          marginBottom: isMobile ? '8px' : '10px'
        }}>
          Total Images (Existing + New): <strong>{imageList.length}</strong>
        </p>

        {uploadErrors.length > 0 && (
          <div style={{ color: 'red', marginBottom: '10px' }}>
            {uploadErrors.map((err, i) => (
              <p key={i}>{err}</p>
            ))}
          </div>
        )}

        <div style={{ marginBottom: isMobile ? '12px' : '10px' }}>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleHostelImageFileChange}
            style={{
              fontSize: isMobile ? '13px' : '16px',
              padding: isMobile ? '5px' : '3px',
              width: isMobile ? '100%' : 'auto'
            }}
          />
        </div>

        <div
          style={{
            border: '1px solid #ddd',
            padding: isMobile ? '10px' : '8px',
            marginBottom: isMobile ? '12px' : '10px',
            borderRadius: '3px'
          }}
        >
          <h5 style={{
            fontSize: isMobile ? '13px' : '16px',
            marginBottom: isMobile ? '8px' : '10px'
          }}>Preview / Manage Images (in current order):</h5>
          {imageList.length === 0 ? (
            <p style={{ color: 'gray' }}>No images yet.</p>
          ) : (
            imageList.map((img, idx) => {
              const isNew = img.isNew;
              const displayUrl = isNew ? URL.createObjectURL(img.file) : img.url;

              return (
                <div
                  key={`${img.name}-${idx}`}
                  style={{
                    marginBottom: isMobile ? '10px' : '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '8px' : '10px',
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                    padding: isMobile ? '8px' : '4px',
                    border: isMobile ? '1px solid #f0f0f0' : 'none',
                    borderRadius: isMobile ? '3px' : '0'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <button
                      onClick={() => handleMoveImage(idx, 'up')}
                      style={{
                        padding: '2px 6px',
                        marginBottom: '2px',
                        cursor: 'pointer',
                      }}
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => handleMoveImage(idx, 'down')}
                      style={{
                        padding: '2px 6px',
                        cursor: 'pointer',
                      }}
                    >
                      ↓
                    </button>
                  </div>

                  {isNew ? (
                    <img
                      src={displayUrl}
                      alt="New Upload Preview"
                      style={{ 
                        maxWidth: isMobile ? '60px' : '80px', 
                        maxHeight: isMobile ? '60px' : '80px',
                        borderRadius: '3px'
                      }}
                    />
                  ) : (
                    <>
                      <ImageValidator url={displayUrl} />
                      <img
                        src={displayUrl}
                        alt="Existing Hostel"
                        style={{ 
                          maxWidth: isMobile ? '60px' : '80px', 
                          maxHeight: isMobile ? '60px' : '80px',
                          borderRadius: '3px'
                        }}
                      />
                    </>
                  )}

                  <span
                    style={{
                      maxWidth: isMobile ? '120px' : '150px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: isMobile ? '12px' : '14px',
                      flex: isMobile ? '1 1 auto' : 'none'
                    }}
                  >
                    {img.name}
                  </span>

                  {!isNew && (
                    <button
                      style={{
                        backgroundColor: '#cc0000',
                        color: '#fff',
                        border: 'none',
                        padding: isMobile ? '6px 8px' : '5px 10px',
                        cursor: 'pointer',
                        fontSize: isMobile ? '12px' : '14px',
                        borderRadius: '3px',
                        minWidth: isMobile ? '60px' : 'auto'
                      }}
                      onClick={() =>
                        handleRemoveExistingImage(selectedHostel.id, img.url, idx)
                      }
                    >
                      Delete
                    </button>
                  )}

                  {isNew && (
                    <>
                      <button
                        style={{
                          backgroundColor: '#cc0000',
                          color: '#fff',
                          border: 'none',
                          padding: isMobile ? '6px 8px' : '5px 10px',
                          cursor: 'pointer',
                          fontSize: isMobile ? '12px' : '14px',
                          borderRadius: '3px',
                          minWidth: isMobile ? '60px' : 'auto'
                        }}
                        onClick={() => handleRemoveNewImage(idx)}
                      >
                        Remove
                      </button>
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: isMobile ? '12px' : '14px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={img.selected}
                          onChange={() => handleToggleSelectNewImage(idx)}
                          style={{
                            width: isMobile ? '14px' : '16px',
                            height: isMobile ? '14px' : '16px'
                          }}
                        />
                        Upload
                      </label>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>

        <button
          onClick={handleSaveAllHostelData}
          disabled={isSaving}
          style={{
            backgroundColor: '#853a38',
            color: 'white',
            border: 'none',
            padding: '10px 15px',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            marginRight: '10px',
          }}
        >
          {isSaving ? 'Saving...' : (selectedHostel.id ? 'Save Hostel' : 'Add Hostel')}
        </button>
      </div>

      {selectedHostel.id && (
        <button
          onClick={() => setShowHostelPreview(true)}
          style={{
            backgroundColor: '#853a38',
            color: 'white',
            border: 'none',
            padding: isMobile ? '10px 15px' : '10px 15px',
            cursor: 'pointer',
            marginBottom: isMobile ? '15px' : '20px',
            borderRadius: '3px',
            fontSize: isMobile ? '13px' : '16px',
            width: isMobile ? '100%' : 'auto',
            minHeight: isMobile ? '36px' : 'auto'
          }}
        >
          Preview with Current Images
        </button>
      )}

      {showHostelPreview && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.4)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: isMobile ? '15px' : '20px',
              borderRadius: '8px',
              maxHeight: '80vh',
              overflowY: 'auto',
              width: isMobile ? '95%' : '90%',
              maxWidth: isMobile ? '350px' : '400px',
              textAlign: 'center',
            }}
          >
            <h3>
              Preview: {selectedHostel.name} in {cityName}
            </h3>
            <HostelPreview
              hostelName={selectedHostel.name}
              cityName={cityName}
              contact={selectedHostel.contact}
              notes={selectedHostel.notes}
              link={selectedHostel.link}
              existingImages={imageList
                .map((img) =>
                  img.isNew && img.selected
                    ? URL.createObjectURL(img.file)
                    : img.url || null
                )
                .filter(Boolean)}
              newFiles={[]}
            />

            <button
              onClick={() => setShowHostelPreview(false)}
              style={{
                marginTop: '20px',
                backgroundColor: '#853a38',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Close Preview
            </button>
          </div>
        </div>
      )}

      <h4 style={{
        fontSize: isMobile ? '16px' : '18px',
        marginTop: isMobile ? '20px' : '25px',
        marginBottom: isMobile ? '10px' : '15px'
      }}>Current Hostels</h4>
      
      {/* Search and Sort Controls */}
      <div style={{ 
        marginBottom: isMobile ? '10px' : '15px',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '8px' : '10px',
        alignItems: isMobile ? 'stretch' : 'center'
      }}>
        <input
          type="text"
          placeholder="Search hostels..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: isMobile ? '6px' : '8px',
            fontSize: isMobile ? '13px' : '14px',
            border: '1px solid #ccc',
            borderRadius: '3px',
            flex: isMobile ? 'none' : '1',
            maxWidth: isMobile ? 'none' : '300px'
          }}
        />
        <div style={{ 
          fontSize: isMobile ? '12px' : '14px',
          color: '#666',
          whiteSpace: 'nowrap'
        }}>
          {filteredAndSortedHostels.length} of {hostels.length} hostels
        </div>
      </div>
      
      <div style={{ 
        overflowX: 'auto',
        marginLeft: isMobile ? '-15px' : '0',
        marginRight: isMobile ? '-15px' : '0',
        paddingLeft: isMobile ? '15px' : '0',
        paddingRight: isMobile ? '15px' : '0',
        WebkitOverflowScrolling: 'touch',
        width: isMobile ? 'calc(100% + 30px)' : '100%'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: isMobile ? '12px' : '14px',
          minWidth: isMobile ? '900px' : 'auto'
        }}>
        <thead>
          <tr>
            <th 
              onClick={() => handleSort('name')}
              style={{ 
                padding: isMobile ? '4px 3px' : '8px', 
                border: '1px solid #ddd', 
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th 
              onClick={() => handleSort('city')}
              style={{ 
                padding: isMobile ? '4px 3px' : '8px', 
                border: '1px solid #ddd', 
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              City {sortField === 'city' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th 
              onClick={() => handleSort('vibe')}
              style={{ 
                padding: isMobile ? '4px 3px' : '8px', 
                border: '1px solid #ddd', 
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Vibe {sortField === 'vibe' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>Cash</th>
            <th style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>Link</th>
            <th 
              onClick={() => handleSort('notes')}
              style={{ 
                padding: isMobile ? '4px 3px' : '8px', 
                border: '1px solid #ddd', 
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Notes {sortField === 'notes' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th 
              onClick={() => handleSort('contact')}
              style={{ 
                padding: isMobile ? '4px 3px' : '8px', 
                border: '1px solid #ddd', 
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Contact {sortField === 'contact' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>Amadeus ID</th>
            <th style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredAndSortedHostels.map((h) => {
            const cityData = cities.find((c) => c.id === h.cityId);
            const vibeLabel = VIBE_OPTIONS.find(v => v.value === (h.vibe || 'clean'))?.label || 'Clean';
            return (
              <tr key={h.id}>
                <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd' }}>{h.name}</td>
                <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd' }}>{cityData ? cityData.name : 'Unknown City'}</td>
                <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd' }}>
                  <span style={{
                    padding: '2px 6px',
                    borderRadius: '4px',
                    backgroundColor: h.vibe === 'lit' ? '#ffebee' : 
                                   h.vibe === 'cozy' ? '#fff3e0' : '#e8f5e9',
                    fontSize: isMobile ? '9px' : '12px'
                  }}>
                    {vibeLabel}
                  </span>
                </td>
                <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', textAlign: 'center' }}>{h.cashRequired ? '💵' : '-'}</td>
                <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd' }}>
                  <a href={h.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                    Link
                  </a>
                </td>
                <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', maxWidth: isMobile ? '80px' : '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{h.notes}</td>
                <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', maxWidth: isMobile ? '80px' : '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{h.contact}</td>
                <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd' }}>{h.amadeusHotelId || ''}</td>
                <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', whiteSpace: 'nowrap' }}>
                  <button 
                    onClick={() => handleEditHostel(h)}
                    style={{
                      padding: isMobile ? '3px 5px' : '5px 8px',
                      marginRight: '4px',
                      fontSize: isMobile ? '10px' : '12px',
                      border: '1px solid #ccc',
                      borderRadius: '2px',
                      backgroundColor: '#f8f9fa',
                      cursor: 'pointer'
                    }}
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDeleteHostel(h.id)}
                    style={{
                      padding: isMobile ? '3px 5px' : '5px 8px',
                      fontSize: isMobile ? '10px' : '12px',
                      border: '1px solid #dc3545',
                      borderRadius: '2px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminJourneyHostels;